import React, { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Download,
  FileSpreadsheet,
  Calendar,
  Printer,
  Filter,
  XCircle,
  Edit3,
  AlertTriangle,
  BarChart3,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  server_post_data,
  get_order_modification_report,
} from "../ServiceConnection/serviceconnection";
import { retrieveData } from "../LocalConnection/LocalConnection.js";
import * as XLSX from "xlsx";
import Loader from "../components/layout/Loader";

const ORDER_STATUS_MAP = {
  0: "Pending",
  1: "Active",
  2: "Completed",
  3: "Cancelled",
};

const PAYMENT_MODE_MAP = {
  0: "Unpaid",
  1: "Cash",
  2: "Card",
  3: "UPI",
  4: "Wallet",
  5: "Online",
};

const ORDER_TYPE_MAP = {
  0: "Dine In",
  1: "Takeaway",
  2: "Delivery",
  3: "Dine In",
  4: "Drive Through",
};

const fmtDate = (val) => {
  if (!val) return "-";
  try {
    return new Date(val).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return val;
  }
};

export default function OrderModificationReport() {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [reportStats, setReportStats] = useState({});
  const [dateFilter, setDateFilter] = useState("today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchItem, setSearchItem] = useState("");

  const restaurantName = retrieveData("restaurant_name") || "UpRestro";
  const restaurantAddress = retrieveData("restaurant_address") || "";

  const fetchReportData = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("start_date", fromDate);
    fd.append("end_date", toDate);

    try {
      const response = await server_post_data(
        get_order_modification_report,
        fd,
      );
      if (!response.data.error) {
        const msg = response.data.message;
        let records = msg.results || [];

        if (searchItem.trim()) {
          records = records.filter((r) =>
            r.item_name?.toLowerCase().includes(searchItem.toLowerCase()),
          );
        }

        setReportData(records);
        setReportStats({
          total_cancelled_items: msg.total_cancelled_items || 0,
          total_modified_items: msg.total_modified_items || 0,
          date_range: msg.date_range || "",
        });
      } else {
        setReportData([]);
        setReportStats({});
      }
    } catch (err) {
      console.error(err);
      setReportData([]);
      setReportStats({});
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, searchItem]);

  // Init today
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) fetchReportData();
  }, [fromDate, toDate]);

  const handleQuickDate = (filter) => {
    setDateFilter(filter);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fmt = (d) => d.toISOString().split("T")[0];

    switch (filter) {
      case "today":
        setFromDate(fmt(today));
        setToDate(fmt(today));
        break;
      case "yesterday": {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        setFromDate(fmt(y));
        setToDate(fmt(y));
        break;
      }
      case "week": {
        const w = new Date(today);
        w.setDate(w.getDate() - 7);
        setFromDate(fmt(w));
        setToDate(fmt(today));
        break;
      }
      case "month": {
        const m = new Date(today);
        m.setMonth(m.getMonth() - 1);
        setFromDate(fmt(m));
        setToDate(fmt(today));
        break;
      }
      default:
        break;
    }
  };

  // ─── EXCEL EXPORT ────────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = {};
    ws["!merges"] = [];
    ws["!rows"] = [];
    ws["!cols"] = [
      { wch: 22 }, // Created
      { wch: 14 }, // Invoice No
      { wch: 14 }, // Order Type
      { wch: 14 }, // Payment Type
      { wch: 20 }, // Customer Name
      { wch: 14 }, // Customer Phone
      { wch: 12 }, // Status
      { wch: 30 }, // Item Name
      { wch: 22 }, // Item Modified Date
      { wch: 22 }, // By User (when_time)
      { wch: 12 }, // Old Value
      { wch: 12 }, // New Value
      { wch: 14 }, // Item Status
      { wch: 22 }, // When
      { wch: 30 }, // Reason
    ];

    let r = 0;

    const setCell = (row, col, value, style = {}) => {
      const addr = XLSX.utils.encode_cell({ r: row, c: col });
      ws[addr] = {
        t: typeof value === "number" ? "n" : "s",
        v: value,
        s: style,
      };
    };

    const COLS = 15;

    // Summary
    const summaryStyle = (bg, txtColor) => ({
      font: { bold: true, sz: 11, color: { rgb: txtColor || "000000" } },
      fill: { fgColor: { rgb: bg } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
    });

    const summaries = [
      [
        "Total Cancelled Items",
        reportStats.total_cancelled_items || 0,
        "FFE6E6",
        "CC0000",
      ],
      [
        "Total Modified Items",
        reportStats.total_modified_items || 0,
        "FFF4E6",
        "996300",
      ],
      ["Total Records", reportData.length, "E6F0FF", "003399"],
    ];

    summaries.forEach(([label, val, bg, txt], i) => {
      const col = i * 5;
      setCell(r, col, label, summaryStyle("D0D8E4", "333333"));
      setCell(r, col + 1, val, summaryStyle(bg, txt));
      ws["!merges"].push({ s: { r: r, c: col }, e: { r: r, c: col + 3 } });
    });
    ws["!rows"][r] = { hpt: 22 };
    r++;
    r++;

    // Table Headers
    const headers = [
      "Created",
      "Invoice No.",
      "Order Type",
      "Payment Type",
      "Customer Name",
      "Customer Phone",
      "Status",
      "Item Name",
      "Item Modified Date",
      "By User",
      "Old Value",
      "New Value",
      "Item Status",
      "When",
      "Reason",
    ];

    const hdrStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
      fill: { fgColor: { rgb: "2C5F8A" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    headers.forEach((h, c) => setCell(r, c, h, hdrStyle));
    ws["!rows"][r] = { hpt: 20 };
    const headerRow = r;
    r++;

    // Data rows
    reportData.forEach((rec, idx) => {
      const isCancelled = rec.item_status === "Cancelled";
      const isModified =
        rec.item_status === "Active" && (rec.old_value || 0) > 0;
      const rowBg = isCancelled ? "FFE6E6" : isModified ? "FFF8E6" : "FFFFFF";
      const altBg = isCancelled ? "FFD6D6" : isModified ? "FFF0D0" : "F5F5F5";
      const bg = idx % 2 === 0 ? rowBg : altBg;

      const cellStyle = (bold = false, color = "000000") => ({
        font: { sz: 10, bold, color: { rgb: color } },
        fill: { fgColor: { rgb: bg } },
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "D0D0D0" } },
          bottom: { style: "thin", color: { rgb: "D0D0D0" } },
          left: { style: "thin", color: { rgb: "D0D0D0" } },
          right: { style: "thin", color: { rgb: "D0D0D0" } },
        },
      });
      const centerStyle = (bold = false, color = "000000") => ({
        ...cellStyle(bold, color),
        alignment: { horizontal: "center", vertical: "center" },
      });

      const row = [
        fmtDate(rec.created),
        rec.invoice_id || "-",
        (ORDER_TYPE_MAP[rec.order_type] || rec.order_type || "-") + (rec.table_code_names ? ` (${rec.table_code_names})` : ""),
        PAYMENT_MODE_MAP[rec.payment_mode] || rec.payment_mode || "-",
        rec.guest_name || "-",
        rec.guest_mobile_no || "-",
        ORDER_STATUS_MAP[rec.order_status] || rec.order_status || "-",
        rec.item_name || "-",
        fmtDate(rec.item_modified_date),
        fmtDate(rec.by_user),
        rec.old_value ?? 0,
        rec.new_value ?? 0,
        rec.item_status || "-",
        fmtDate(rec.when_time),
        rec.reason || "-",
      ];

      row.forEach((val, c) => {
        let style;
        if (c === 1)
          style = cellStyle(true, "1E3A8A"); // Invoice bold blue
        else if (c === 7)
          style = cellStyle(false); // Item Name
        else if (c === 10 || c === 11)
          style = centerStyle(true); // Old/New qty
        else if (c === 12) {
          style = centerStyle(true, isCancelled ? "CC0000" : "996300");
        } else {
          style = cellStyle();
        }
        setCell(r, c, val, style);
      });
      r++;
    });

    // Footer
    r++;
    setCell(
      r,
      0,
      `Total Cancelled Items: ${reportStats.total_cancelled_items || 0}   |   Total Modified Items: ${reportStats.total_modified_items || 0}   |   Total Records: ${reportData.length}`,
      {
        font: { bold: true, sz: 10, italic: true },
        fill: { fgColor: { rgb: "E8EEF4" } },
      },
    );
    ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: COLS - 1 } });

    // Set sheet range
    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r, c: COLS - 1 },
    });

    XLSX.utils.book_append_sheet(wb, ws, "Order Modifications");
    XLSX.writeFile(
      wb,
      `Order_Modification_Report_${fromDate}_to_${toDate}.xlsx`,
    );
  };

  const cancelledCount = reportData.filter(
    (r) => r.item_status === "Cancelled",
  ).length;
  const modifiedCount = reportData.filter(
    (r) => r.item_status === "Active",
  ).length;

  return (
    <MainLayout>
      <Header
        title="Order Modification Report"
        subtitle="Track order modifications and cancellations"
      />
      {loading && <Loader />}

      <div className="p-6 space-y-6">
        {/* Filters */}
        <Card className="border-primary/20 print:hidden">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Report Filters
                </CardTitle>
                <CardDescription>
                  Configure your report parameters
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => window.print()}
                  variant="outline"
                  className="gap-2"
                >
                  <Printer className="w-4 h-4" /> Print
                </Button>
                <Button onClick={exportToExcel} className="gap-2">
                  <Download className="w-4 h-4" /> Export Excel
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Item Search */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Search by Item</Label>
                <Input
                  placeholder="e.g., Butter Naan"
                  value={searchItem}
                  onChange={(e) => setSearchItem(e.target.value)}
                />
              </div>
            </div>

            {/* Date Filters */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Report Period
              </Label>
              <div className="flex flex-wrap gap-2">
                {["today", "yesterday", "week", "month", "custom"].map((f) => (
                  <Button
                    key={f}
                    variant={dateFilter === f ? "default" : "outline"}
                    size="sm"
                    onClick={() =>
                      f === "custom"
                        ? setDateFilter("custom")
                        : handleQuickDate(f)
                    }
                  >
                    {
                      {
                        today: "Today",
                        yesterday: "Yesterday",
                        week: "Last 7 Days",
                        month: "Last 30 Days",
                        custom: "Custom Range",
                      }[f]
                    }
                  </Button>
                ))}
              </div>

              {dateFilter === "custom" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg border">
                  <div className="space-y-2">
                    <Label>From Date</Label>
                    <Input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To Date</Label>
                    <Input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      min={fromDate}
                    />
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={fetchReportData}
              className="w-full gap-2"
              size="lg"
            >
              <BarChart3 className="w-4 h-4" /> Generate Report
            </Button>
          </CardContent>
        </Card>

        {/* Report Content */}
        <div className="space-y-6 print:space-y-4">
          {/* Header Card */}
          <Card className="border-2 print:border-0 print:shadow-none">
            <CardContent className="pt-6">
              <div className="text-center space-y-1 border-b pb-4 mb-4">
                <h1 className="text-2xl font-bold text-primary print:text-black">
                  {restaurantName}
                </h1>
                {restaurantAddress && (
                  <p className="text-sm text-muted-foreground">
                    {restaurantAddress}
                  </p>
                )}
                <h2 className="text-lg font-semibold mt-2">
                  ORDER MODIFICATION & CANCELLED KOT REPORT
                </h2>
                <p className="text-sm text-muted-foreground">
                  Period:{" "}
                  <strong>
                    {reportStats.date_range || `${fromDate} to ${toDate}`}
                  </strong>
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-xs text-red-700">Items Cancelled</p>
                  <p className="text-2xl font-bold text-red-600">
                    {reportStats.total_cancelled_items || 0}
                  </p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <p className="text-xs text-amber-700">Items Modified</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {reportStats.total_modified_items || 0}
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-xs text-blue-700">Total Records</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {reportData.length}
                  </p>
                </div>
                <div className="bg-primary/5 p-4 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">
                    Active (Modified)
                  </p>
                  <p className="text-2xl font-bold text-primary">
                    {modifiedCount}
                  </p>
                </div>
              </div>

              {reportStats.total_cancelled_items > 10 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                  <p className="text-sm text-red-700">
                    High Cancellation Alert: {reportStats.total_cancelled_items}{" "}
                    items cancelled. Consider reviewing order processes.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="print:border-0 print:shadow-none">
            <CardHeader className="print:pb-2">
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Detailed Modification Records
              </CardTitle>
              <CardDescription>
                Showing {reportData.length} records
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-auto print:border-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 print:bg-gray-100">
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Invoice No.</TableHead>
                      <TableHead>Order Type</TableHead>
                      <TableHead>Payment Type</TableHead>
                      <TableHead>Customer Name</TableHead>
                      <TableHead>Customer Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Item Name</TableHead>
                      <TableHead>Item Modified Date</TableHead>
                      <TableHead>By User</TableHead>
                      <TableHead className="text-center">Old Value</TableHead>
                      <TableHead className="text-center">New Value</TableHead>
                      <TableHead className="text-center">Item Status</TableHead>
                      <TableHead>When</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={16}
                          className="text-center py-12 text-muted-foreground"
                        >
                          No modifications or cancellations found for selected
                          period
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportData.map((rec, idx) => {
                        const isCancelled = rec.item_status === "Cancelled";
                        return (
                          <TableRow
                            key={idx}
                            className={
                              isCancelled ? "bg-red-50/50" : "bg-amber-50/30"
                            }
                          >
                            <TableCell className="font-medium">
                              {idx + 1}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {fmtDate(rec.created)}
                            </TableCell>
                            <TableCell className="font-medium text-primary">
                              {rec.invoice_id || "-"}
                            </TableCell>
                            <TableCell>
                              {ORDER_TYPE_MAP[rec.order_type] ||
                                rec.order_type ||
                                "-"}{rec.table_code_names ? ` (${rec.table_code_names})` : ""}
                            </TableCell>
                            <TableCell>
                              {PAYMENT_MODE_MAP[rec.payment_mode] ||
                                rec.payment_mode ||
                                "-"}
                            </TableCell>
                            <TableCell>{rec.guest_name || "-"}</TableCell>
                            <TableCell>{rec.guest_mobile_no || "-"}</TableCell>
                            <TableCell>
                              {ORDER_STATUS_MAP[rec.order_status] ||
                                rec.order_status ||
                                "-"}
                            </TableCell>
                            <TableCell className="font-medium">
                              {rec.item_name}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {fmtDate(rec.item_modified_date)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {fmtDate(rec.by_user)}
                            </TableCell>
                            <TableCell className="text-center font-medium">
                              {rec.old_value ?? 0}
                            </TableCell>
                            <TableCell className="text-center font-medium">
                              {rec.new_value ?? 0}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge
                                className={cn(
                                  "font-medium border gap-1",
                                  isCancelled
                                    ? "bg-red-100 text-red-700 border-red-300"
                                    : "bg-amber-100 text-amber-700 border-amber-300",
                                )}
                              >
                                {isCancelled ? (
                                  <XCircle className="w-3 h-3" />
                                ) : (
                                  <Edit3 className="w-3 h-3" />
                                )}
                                {rec.item_status}
                              </Badge>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {fmtDate(rec.when_time)}
                            </TableCell>
                            <TableCell
                              className="max-w-[180px] truncate"
                              title={rec.reason}
                            >
                              {rec.reason || "-"}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 pt-4 border-t flex justify-between text-sm text-muted-foreground">
                <span>
                  Total Records: <strong>{reportData.length}</strong>
                </span>
                <span>
                  Cancelled:{" "}
                  <strong className="text-red-600">{cancelledCount}</strong>
                  &nbsp;|&nbsp;Modified:{" "}
                  <strong className="text-amber-600">{modifiedCount}</strong>
                </span>
                <span>
                  Generated:{" "}
                  {new Date().toLocaleString("en-IN", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
